import React, { useMemo } from 'react';
import type { Production, ProductionInput } from 'sr-types/lib/production/v1/graphql';
import AttributesSidePanel from '../attributes/AttributesSidePanel';
import type { EntityName } from '../common/auth/Security';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import { useProductionDetailsData } from './helpers/productionApi';

type ProductionAttributesSidePanelProps = {
    entityId: string;
    entityName: string;
    entity?: EntityName;
    onClose: () => void;
    onSaveEntity: (values: Production & ProductionInput, isDeletingAttribute: boolean) => Promise<string | undefined>;
    /**
     * Attributes already known to be current - kept by the production page itself as it saves
     * (an IP Deal's Production Info step, or this very panel's own edits) - layered over
     * whatever this panel's own `detailsQuery` last fetched.
     *
     * That query runs `fetchPolicy: 'no-cache'` (see `useProductionDetailsData`), so it is never
     * updated by another mutation's result and is only ever as fresh as when this panel mounted.
     * Without this override, attributes saved elsewhere while the panel is already open would
     * not appear until it is closed and reopened, forcing a fresh fetch.
     */
    attributesOverride?: Pick<ProductionInput, 'attributes' | 'associatedFieldDefinitionReferences'>;
};

export default function ProductionAttributesSidePanel({
    entityId,
    entityName,
    entity = ENTITY_CONSTANTS.production,
    onClose,
    onSaveEntity,
    attributesOverride
}: ProductionAttributesSidePanelProps) {
    const { productionDetailsData, productionDetailsLoading } = useProductionDetailsData(entityId, !entityId);
    const production = productionDetailsData?.production;

    const entityValues = useMemo(() => {
        if (!production || !attributesOverride) return production;
        return {
            ...production,
            attributes: attributesOverride.attributes ?? production.attributes,
            associatedFieldDefinitionReferences:
                attributesOverride.associatedFieldDefinitionReferences ?? production.associatedFieldDefinitionReferences
        };
    }, [production, attributesOverride]);

    return (
        <AttributesSidePanel<Production & ProductionInput>
            entityId={entityId}
            entityName={entityName || production?.name || ''}
            entity={entity}
            entityValues={entityValues}
            isEntityLoading={productionDetailsLoading}
            onClose={onClose}
            onSaveEntity={onSaveEntity}
        />
    );
}
